import { useState } from "react";
import {
  BELGE_KOLONLARI,
  BELGE_TURLERI,
  BELGE_TURU_ADI,
  KAYNAKCI_STANDARTLARI,
  NDT_SEVIYELERI,
  NDT_YONTEMLERI,
  ayniTurdeGuncelVarMi,
  belgeAlanlari,
  belgeRozetleri,
  yenisiGeldiTarihEtkisi,
  type BelgeAlani,
  type BelgeKutugu,
  type BelgeTuru,
  type KutukBelgeSatiri,
  type KutukBelgeYaniti,
} from "@oasis/domain";
import { api } from "@/lib/api";
import { cn } from "@/lib/cn";
import { cevir } from "@/lib/dil";
import { belgeCipiMetni } from "@/lib/stok-paket";
import { dosyaSec, dosyaYukle, tarih, tarihSaat } from "@/lib/veri";
import { Buton, Etiket, Hata, etiket, girdi, tabloBas, tabloHucre } from "@/components/parcalar";
import { belgeLinki, sayfaEtiketi } from "@/components/pdf-sayfa";
import { CIP_SINIFI, rozetSinifi, taslakEksigi, taslakGirdisi, yeniTaslak, type BelgeTaslagi } from "@/components/kutuk-kullanim";

/**
 * D-048 / D-049: kütük kaydının "Documents" bölümü (stok paketi, sarf lotu, kaynakçı, alet, WPS) ve tablo hücresindeki çipler.
 * Kayıtlı belgeler salt okunur satırdır (PDF bağlantı, "Remove" bağı kaldırır); altında Type seçimi, "+ Upload PDF", "+ Use existing PDF";
 * açılır "Removed documents (n)". Ekleme ve kaldırma ayrı uçlar (POST /kutuk/belge, /kutuk/belge/:id/kaldir); kaydın Save'i belgeleri silip
 * yeniden yazmaz. Yeni kayıtta (anahtar null) seçilen PDF'ler taslak kalır, üst bileşen kaydı kaydederken birlikte gönderir.
 * D-064: NDT inspektörünün sertifika satırında Method ve Level seçim kutusudur ve zorunludur: kayıtlı kayıtta ikisi seçilmeden "Add"
 * pasiftir (ipucu eksik seçimi yazar). Yeni kayıtta ve pencerenin Save'inde engel sunucunun cevabıdır ("Documents: select the method").
 * D-074 Faz 2 (K16, K23): güncel belgenin satırında "Mark as superseded"; işaretli belgeler "Superseded documents (n)" altında, "Superseded"
 * etiketi, kim ve ne zaman ile görünür kalır; "Mark as current" geri alır. İkisi de onaylıdır; onay metni kaydın tarihine etkisini (D-049)
 * önceden yazar (`yenisiGeldiOnayi`).
 */

type PdfSecenegi = { id: string; ad: string; toplam: number };

const sayfaAraligi = (b: { sayfaBas: number | null; sayfaSon: number | null }) =>
  b.sayfaBas ? { sayfaBas: b.sayfaBas, sayfaSon: b.sayfaSon ?? b.sayfaBas } : null;

const TARIHLI: readonly BelgeAlani[] = ["tarih", "gecerlilikSonu"];

/**
 * D-074 Faz 2: "Mark as superseded" / "Mark as current" onay metni (window.confirm; satırlar \n ile). Sıra: soru, etki, tarih (yalnız
 * tarih kuralı olan kütükte; D-049 sunucudaki kuralla aynı hesap), son olarak işaretlemede aynı türden güncel belge kalmıyorsa uyarı.
 */
export function yenisiGeldiOnayi(g: { kutuk: BelgeKutugu; belge: KutukBelgeSatiri; guncel: readonly KutukBelgeSatiri[]; kayitTarihi: string | null; isaret: boolean }): string {
  const satirlar = g.isaret
    ? [cevir("Mark “{0}” as superseded?", g.belge.ad), cevir("It stays on this record under Superseded documents with its PDF. OASIS no longer uses it as a current document: Riser Documentation does not list it; alerts and the NDT signer check do not count it.")]
    : [cevir("Make “{0}” a current document of this record again?", g.belge.ad), cevir("OASIS uses it again as a current document (Riser Documentation, alerts, NDT signer check).")];
  const e = yenisiGeldiTarihEtkisi(g.kutuk, g.kayitTarihi, g.guncel, g.belge, g.isaret);
  if (e && (e.once || e.sonra)) {
    satirlar.push(e.once === e.sonra
      ? cevir("{0} stays {1}.", cevir(e.alan), tarih(e.once!))
      : cevir("{0}: {1} → {2} (taken from the latest current certificate).", cevir(e.alan), e.once ? tarih(e.once) : "—", tarih(e.sonra!)));
  }
  if (g.isaret && !ayniTurdeGuncelVarMi(g.guncel, g.belge)) {
    const ad = BELGE_TURU_ADI[g.kutuk][g.belge.tur as BelgeTuru];
    satirlar.push(cevir("No other current document of this type remains on this record: {0}.", ad ? cevir(ad) : g.belge.tur));
  }
  return satirlar.join("\n");
}

/** Tablo hücresi: güncel belgelerin çipleri (PDF'e bağlantı) ve eksik belge rozetleri; ikisi de yoksa "—" (WPS). */
export function BelgeCipleri({ kutuk, belgeler, poNo }: { kutuk: BelgeKutugu; belgeler: KutukBelgeSatiri[]; poNo?: string | null }) {
  const rozetler = belgeRozetleri(kutuk, belgeler, { poNo });
  if (!belgeler.length && !rozetler.length) return <span className="text-ink-3">—</span>;
  return (
    <span className="flex flex-wrap gap-1 whitespace-nowrap">
      {belgeler.map((b) => (
        <a key={b.id} className={cn(CIP_SINIFI, "hover:underline")} href={belgeLinki(b.dokumanId, sayfaAraligi(b))} target="_blank" rel="noreferrer" title={b.ad}>
          {belgeCipiMetni(b)}
        </a>
      ))}
      {rozetler.map((r) => <span key={r} className={rozetSinifi(r)}>{cevir(r)}</span>)}
    </span>
  );
}

export function KutukBelgeleri({
  kutuk,
  anahtar,
  belgeler,
  yenisiGelenBelgeler,
  kaldirilanBelgeler,
  taslaklar,
  onTaslak,
  onDegisti,
  kilit,
  ekAciklama,
  onMesgul,
  kayitTarihi,
}: {
  kutuk: BelgeKutugu;
  /** Kayıtlı kaydın anahtarı (stok paketi: paket id · lot · kod); yeni kayıtta null (taslak modu). */
  anahtar: string | null;
  belgeler: KutukBelgeSatiri[];
  /** D-074 Faz 2: yerine yenisi gelen ("Superseded") belgeler */
  yenisiGelenBelgeler: KutukBelgeSatiri[];
  kaldirilanBelgeler: KutukBelgeSatiri[];
  taslaklar: BelgeTaslagi[];
  onTaslak: (f: (eski: BelgeTaslagi[]) => BelgeTaslagi[]) => void;
  /** Ekle / kaldır / işaretle sonrası kaydın güncel belge listeleri ve (kaynakçı / alet) kaydın tarihi. */
  onDegisti?: (y: KutukBelgeYaniti) => void;
  kilit?: boolean;
  /** Kütüğün tarih kuralı cümlesi (kaynakçı, alet); genel açıklamanın altında. */
  ekAciklama?: string | null;
  /**
   * Bölümün işi sürerken (PDF yükleme, ekle, kaldır) true, bitince false. Üst pencere bu sırada Save / Cancel'ı kapatır ve Modal'ı
   * kilitler: yükleme bitmeden kaydedilen kayıtta taslak yer almaz, PDF hiçbir kayda bağlanmadan kalırdı.
   */
  onMesgul?: (mesgul: boolean) => void;
  /** D-074 Faz 2: kaydın formdaki tarihi (kaynakçı End of validity, alet Calibration end; "YYYY-MM-DD" ya da null); onay metni D-049 etkisini bununla yazar. */
  kayitTarihi?: string | null;
}) {
  const turler: readonly string[] = BELGE_TURLERI[kutuk];
  const kolonlar = BELGE_KOLONLARI[kutuk];
  const [tur, setTur] = useState<string>(turler[0] ?? "");
  const [pdfler, setPdfler] = useState<PdfSecenegi[] | null>(null);
  const [mesgul, setMesgul] = useState<string | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const kayitli = anahtar !== null;
  const kapali = Boolean(kilit) || mesgul !== null;

  const turAdi = (t: string) => {
    const ad = BELGE_TURU_ADI[kutuk][t as BelgeTuru];
    return ad ? cevir(ad) : t;
  };

  const dene = async (is: string, f: () => Promise<void>) => {
    setMesgul(is);
    onMesgul?.(true);
    setHata(null);
    try {
      await f();
    } catch (e) {
      setHata(e instanceof Error ? e.message : cevir("Error"));
    } finally {
      setMesgul(null);
      onMesgul?.(false);
    }
  };

  const taslakYaz = (kimlik: number, d: Partial<BelgeTaslagi>) => onTaslak((l) => l.map((t) => (t.kimlik === kimlik ? { ...t, ...d } : t)));
  const taslakSil = (kimlik: number) => onTaslak((l) => l.filter((t) => t.kimlik !== kimlik));

  const yukle = async () => {
    const f = await dosyaSec(".pdf,application/pdf");
    if (!f) return;
    await dene("yukle", async () => {
      const d = await dosyaYukle<PdfSecenegi>("/kutuk/belge/yukle", f, { tur });
      onTaslak((l) => [...l, yeniTaslak(tur, d)]);
      setPdfler((l) => (l && !l.some((x) => x.id === d.id) ? [...l, d] : l));
    });
  };

  const mevcut = () =>
    dene("mevcut", async () => {
      if (!pdfler) setPdfler(await api.get<PdfSecenegi[]>("/kutuk/belge/pdfler"));
      onTaslak((l) => [...l, yeniTaslak(tur, null)]);
    });

  const ekle = (t: BelgeTaslagi) =>
    dene(`t${t.kimlik}`, async () => {
      const y = await api.post<KutukBelgeYaniti>("/kutuk/belge", { kutuk, anahtar, ...taslakGirdisi(t) });
      taslakSil(t.kimlik);
      onDegisti?.(y);
    });

  const kaldir = (b: KutukBelgeSatiri) =>
    dene(b.id, async () => {
      onDegisti?.(await api.post<KutukBelgeYaniti>(`/kutuk/belge/${b.id}/kaldir`));
    });

  /** D-074 Faz 2: "Mark as superseded" (isaret true) / "Mark as current" (false); onaydan sonra. */
  const yenisiGeldi = (b: KutukBelgeSatiri, isaret: boolean) => {
    if (!window.confirm(yenisiGeldiOnayi({ kutuk, belge: b, guncel: belgeler, kayitTarihi: kayitTarihi ?? null, isaret }))) return;
    void dene(b.id, async () => {
      onDegisti?.(await api.post<KutukBelgeYaniti>(`/kutuk/belge/${b.id}/${isaret ? "yenisi-geldi" : "guncel"}`));
    });
  };

  /** Kayıtlı satırın kütüğe özgü hücresi; alan o türde yoksa ya da boşsa "—". */
  const alanHucresi = (b: KutukBelgeSatiri, alan: BelgeAlani) => {
    const v = belgeAlanlari(kutuk, b.tur).includes(alan) ? b[alan] : null;
    if (!v) return <td key={alan} className={cn(tabloHucre, "text-ink-3")}>—</td>;
    const tarihli = TARIHLI.includes(alan);
    return <td key={alan} className={cn(tabloHucre, alan !== "not" && "whitespace-nowrap", tarihli && "tnum")}>{tarihli ? tarih(v) : v}</td>;
  };

  /** Taslak satırın kütüğe özgü girişi; alan o türde yoksa "—" (ör. süreklilik kaydında Standard ve End of validity). */
  const alanGirisi = (t: BelgeTaslagi, alan: BelgeAlani) => {
    if (!belgeAlanlari(kutuk, t.tur).includes(alan)) return <td key={alan} className={cn(tabloHucre, "text-ink-3")}>—</td>;
    const yaz = (v: string) => taslakYaz(t.kimlik, { [alan]: v } as Partial<BelgeTaslagi>);
    // D-064: NDT sertifikasının yöntemi ve seviyesi listeden seçilir (zorunlu; kısaltmalar ve rakamlar çevrilmez).
    if (alan === "yontem" || alan === "seviye") {
      const secenekler: readonly string[] = alan === "yontem" ? NDT_YONTEMLERI : NDT_SEVIYELERI;
      return (
        <td key={alan} className={tabloHucre}>
          <select className={cn(girdi, "w-24")} aria-label={alan === "yontem" ? cevir("Method") : cevir("Level")} value={t[alan]} onChange={(e) => yaz(e.target.value)}>
            <option value="">—</option>
            {secenekler.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </td>
      );
    }
    if (alan === "standart") {
      return (
        <td key={alan} className={tabloHucre}>
          <select className={cn(girdi, "w-40")} value={t.standart} onChange={(e) => yaz(e.target.value)}>
            <option value="">—</option>
            {KAYNAKCI_STANDARTLARI.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </td>
      );
    }
    const tarihli = TARIHLI.includes(alan);
    return (
      <td key={alan} className={tabloHucre}>
        <input className={cn(girdi, tarihli ? "w-36" : alan === "not" ? "w-48" : "w-32")} type={tarihli ? "date" : "text"} value={t[alan]} onChange={(e) => yaz(e.target.value)} />
      </td>
    );
  };

  const baslik = (
    <tr className="border-b border-line">
      <th className={tabloBas}>{cevir("Type")}</th>
      <th className={tabloBas}>PDF</th>
      <th className={tabloBas}>{cevir("Pages")}</th>
      <th className={tabloBas}>{cevir("Doc no.")}</th>
      {kolonlar.map((k) => <th key={k.alan} className={tabloBas}>{cevir(k.etiket)}</th>)}
      <th className={tabloBas} />
    </tr>
  );
  const pdfBaglantisi = (b: KutukBelgeSatiri) => (
    <a className="text-sm text-orange underline" href={belgeLinki(b.dokumanId, sayfaAraligi(b))} target="_blank" rel="noreferrer">{b.ad}</a>
  );
  const sayfaHucresi = (b: KutukBelgeSatiri) => {
    const a = sayfaAraligi(b);
    return a ? <td className={cn(tabloHucre, "whitespace-nowrap")}>{sayfaEtiketi(a)}</td> : <td className={cn(tabloHucre, "text-ink-3")}>—</td>;
  };
  const noHucresi = (b: KutukBelgeSatiri) =>
    b.belgeNo ? <td className={cn(tabloHucre, "whitespace-nowrap")}>{b.belgeNo}</td> : <td className={cn(tabloHucre, "text-ink-3")}>—</td>;
  /** Taslak satırın "Add" düğmesi (yalnız kayıtlı kayıtta). D-064: zorunlu seçim eksikken pasiftir, ipucu eksiği yazar ("select the method" / "select the level"). */
  const ekleDugmesi = (t: BelgeTaslagi) => {
    const eksik = taslakEksigi(kutuk, t);
    return (
      <Buton kucuk tur="birincil" disabled={kapali || !t.dokumanId || eksik !== null} title={eksik ? cevir(eksik) : undefined} mesgul={mesgul === `t${t.kimlik}`} onClick={() => ekle(t)}>
        {cevir("Add")}
      </Buton>
    );
  };
  /** D-064: NDT inspektöründe iki kolon daha var (Method, Level); tablo daha geniş açılır, dar ekranda yatay kayar. */
  const tabloGenisligi = kutuk === "ndt_inspektor" ? "min-w-[900px]" : kolonlar.length ? "min-w-[720px]" : "min-w-[640px]";

  return (
    <>
      <div className="mt-4">
        <label className={etiket}>{cevir("Documents")}</label>
        {belgeler.length + taslaklar.length > 0 && (
          <div className="scroll-thin overflow-x-auto">
            <table className={cn("w-full", tabloGenisligi)}>
              <thead>{baslik}</thead>
              <tbody>
                {belgeler.map((b) => (
                  <tr key={b.id} className="border-b border-line last:border-0">
                    <td className={tabloHucre}>{turAdi(b.tur)}</td>
                    <td className={tabloHucre}>{pdfBaglantisi(b)}</td>
                    {sayfaHucresi(b)}
                    {noHucresi(b)}
                    {kolonlar.map((k) => alanHucresi(b, k.alan))}
                    <td className={tabloHucre}>
                      {kayitli && (
                        <span className="flex items-center justify-end gap-1.5 whitespace-nowrap">
                          <Buton kucuk disabled={kapali} mesgul={mesgul === b.id} onClick={() => yenisiGeldi(b, true)}>{cevir("Mark as superseded")}</Buton>
                          <Buton kucuk disabled={kapali} mesgul={mesgul === b.id} onClick={() => kaldir(b)}>{cevir("Remove")}</Buton>
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
                {taslaklar.map((t) => (
                  <tr key={`t${t.kimlik}`} className="border-b border-line bg-surface-2/60 last:border-0">
                    <td className={tabloHucre}>{turAdi(t.tur)}</td>
                    <td className={tabloHucre}>
                      {t.mevcuttan ? (
                        <select
                          className={cn(girdi, "min-w-48")}
                          value={t.dokumanId}
                          onChange={(e) => {
                            const p = (pdfler ?? []).find((x) => x.id === e.target.value);
                            taslakYaz(t.kimlik, { dokumanId: e.target.value, ad: p?.ad ?? "", toplam: p?.toplam ?? 0 });
                          }}
                        >
                          <option value="">{cevir("Select...")}</option>
                          {(pdfler ?? []).map((x) => <option key={x.id} value={x.id}>{x.ad}{x.toplam ? cevir` (${x.toplam} pages)` : ""}</option>)}
                        </select>
                      ) : (
                        <a className="text-sm text-orange underline" href={belgeLinki(t.dokumanId)} target="_blank" rel="noreferrer">{t.ad}</a>
                      )}
                    </td>
                    <td className={tabloHucre}>
                      <span className="flex items-center gap-1">
                        <span className="text-xs text-ink-3">p.</span>
                        <input className={cn(girdi, "w-16")} type="number" min={1} title={cevir("From page")} aria-label={cevir("From page")} value={t.sayfaBas} onChange={(e) => taslakYaz(t.kimlik, { sayfaBas: e.target.value })} />
                        –
                        <input className={cn(girdi, "w-16")} type="number" min={1} title={cevir("To page")} aria-label={cevir("To page")} value={t.sayfaSon} onChange={(e) => taslakYaz(t.kimlik, { sayfaSon: e.target.value })} />
                      </span>
                    </td>
                    <td className={tabloHucre}>
                      <input className={cn(girdi, "w-32")} value={t.belgeNo} onChange={(e) => taslakYaz(t.kimlik, { belgeNo: e.target.value })} />
                    </td>
                    {kolonlar.map((k) => alanGirisi(t, k.alan))}
                    <td className={tabloHucre}>
                      <span className="flex items-center justify-end gap-1.5 whitespace-nowrap">
                        {kayitli && ekleDugmesi(t)}
                        <Buton kucuk disabled={kapali} title={cevir("Remove")} onClick={() => taslakSil(t.kimlik)}>✕</Buton>
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <span className="text-sm text-ink-3">{cevir("Type")}</span>
          <select className={cn(girdi, "max-w-sm")} value={tur} disabled={kapali} onChange={(e) => setTur(e.target.value)}>
            {turler.map((t) => <option key={t} value={t}>{turAdi(t)}</option>)}
          </select>
          <Buton kucuk disabled={kapali} mesgul={mesgul === "yukle"} onClick={yukle}>{mesgul === "yukle" ? cevir("Uploading...") : cevir("+ Upload PDF")}</Buton>
          <Buton kucuk disabled={kapali} mesgul={mesgul === "mevcut"} onClick={mevcut}>{cevir("+ Use existing PDF")}</Buton>
        </div>
        <p className="mt-1 text-xs text-ink-3">
          {cevir("Pages are optional: empty = the whole PDF. A book is uploaded once and then picked with \"Use existing PDF\" for every record in it. Remove only unlinks the document; it stays under Removed documents.")}
        </p>
        {kayitli && (
          <p className="mt-1 text-xs text-ink-3">
            {cevir("Mark as superseded when a newer document replaces this one: it stays under Superseded documents, and OASIS uses only the current documents (record date, alerts, NDT signer check, Riser Documentation).")}
          </p>
        )}
        {ekAciklama && <p className="mt-1 text-xs text-ink-3">{cevir(ekAciklama)}</p>}
        {kutuk === "kaynakci" && (
          <p className="mt-1 text-xs text-ink-3">{cevir("End of validity may be left empty: an ASME IX qualification has no end date and stays valid while continuity is recorded.")}</p>
        )}
        {kutuk === "ndt_inspektor" && (
          <p className="mt-1 text-xs text-ink-3">
            {cevir("Method and Level are required for an NDT certificate. When an NDT report is uploaded, the certificates of the selected signer are checked: the method (where the step name states it), Level 2 or 3, End of validity not passed. If they are not sufficient, OASIS warns and still accepts the upload. A certificate with an empty End of validity is not checked for expiry. A certificate saved without method or level cannot be edited: remove it and add it again with \"Use existing PDF\".")}
          </p>
        )}
        {hata && <div className="mt-2"><Hata mesaj={hata} /></div>}
      </div>
      {yenisiGelenBelgeler.length > 0 && (
        <div className="mt-3">
          <label className={etiket}>{cevir("Superseded documents ({0})", yenisiGelenBelgeler.length)}</label>
          <div className="scroll-thin overflow-x-auto">
            <table className={cn("w-full", tabloGenisligi)}>
              <thead>
                <tr className="border-b border-line">
                  <th className={tabloBas}>{cevir("Type")}</th>
                  <th className={tabloBas}>PDF</th>
                  <th className={tabloBas}>{cevir("Pages")}</th>
                  <th className={tabloBas}>{cevir("Doc no.")}</th>
                  {kolonlar.map((k) => <th key={k.alan} className={tabloBas}>{cevir(k.etiket)}</th>)}
                  <th className={tabloBas}>{cevir("Superseded")}</th>
                  <th className={tabloBas} />
                </tr>
              </thead>
              <tbody>
                {yenisiGelenBelgeler.map((b) => (
                  <tr key={b.id} className="border-b border-line text-ink-2 last:border-0">
                    <td className={tabloHucre}>
                      <span className="flex flex-wrap items-center gap-1.5">{turAdi(b.tur)} <Etiket>{cevir("Superseded")}</Etiket></span>
                    </td>
                    <td className={tabloHucre}>{pdfBaglantisi(b)}</td>
                    {sayfaHucresi(b)}
                    {noHucresi(b)}
                    {kolonlar.map((k) => alanHucresi(b, k.alan))}
                    <td className={cn(tabloHucre, "whitespace-nowrap")}>{tarihSaat(b.yenisiGeldiTs)} · {b.yenisiGeldiKim ?? "—"}</td>
                    <td className={tabloHucre}>
                      {kayitli && (
                        <span className="flex items-center justify-end gap-1.5 whitespace-nowrap">
                          <Buton kucuk disabled={kapali} mesgul={mesgul === b.id} onClick={() => yenisiGeldi(b, false)}>{cevir("Mark as current")}</Buton>
                          <Buton kucuk disabled={kapali} mesgul={mesgul === b.id} onClick={() => kaldir(b)}>{cevir("Remove")}</Buton>
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <details className="group mt-3">
        <summary className="mb-2 cursor-pointer list-none text-sm font-semibold text-ink-2 hover:text-ink">
          <span className="inline-block w-4 transition group-open:rotate-90">▶</span>
          {cevir("Removed documents ({0})", kaldirilanBelgeler.length)}
        </summary>
        {kaldirilanBelgeler.length > 0 && (
          <div className="scroll-thin overflow-x-auto">
            <table className={cn("w-full", tabloGenisligi)}>
              <thead>
                <tr className="border-b border-line">
                  <th className={tabloBas}>{cevir("Type")}</th>
                  <th className={tabloBas}>PDF</th>
                  <th className={tabloBas}>{cevir("Pages")}</th>
                  <th className={tabloBas}>{cevir("Doc no.")}</th>
                  {kolonlar.map((k) => <th key={k.alan} className={tabloBas}>{cevir(k.etiket)}</th>)}
                  <th className={tabloBas}>{cevir("Removed")}</th>
                </tr>
              </thead>
              <tbody>
                {kaldirilanBelgeler.map((b) => (
                  <tr key={b.id} className="border-b border-line text-ink-3 last:border-0">
                    <td className={tabloHucre}>{turAdi(b.tur)}</td>
                    <td className={tabloHucre}>{pdfBaglantisi(b)}</td>
                    {sayfaHucresi(b)}
                    {noHucresi(b)}
                    {kolonlar.map((k) => alanHucresi(b, k.alan))}
                    <td className={cn(tabloHucre, "whitespace-nowrap")}>{tarihSaat(b.kaldirildiTs)} · {b.kaldiran ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </details>
    </>
  );
}
